import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiCpu, FiTrendingUp, FiBookmark, FiShare2 } from 'react-icons/fi';
import { getFavorites, updateFavorite, getUserWallets, getWalletBalance } from './utils/api';
import { buildQuickTradeDeeplink } from './utils/marketUtils';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './hooks/useToast';
import { useSearch } from './hooks/useSearch';
import { useMarketData } from './hooks/useMarketData';
import { useFavorites } from './hooks/useFavorites';
import { useFavoriteDetail } from './hooks/useFavoriteDetail';
import { useSingleMarket } from './hooks/useSingleMarket';
import { useTraderActivityWebSocket } from './hooks/useTraderActivityWebSocket';
import { LoginModal } from './components/LoginModal';
import { InviteCodeModal } from './components/InviteCodeModal';
import { EmailBindModal } from './components/EmailBindModal';
import { CategorySelectionModal } from './components/CategorySelectionModal';
import { AIInsightModal } from './components/AIInsightModal';
import { ShareMarketModal } from './components/ShareMarketModal';
import { FloatingActionBar } from './components/FloatingActionBar';
import { Sidebar } from './components/Sidebar';
import { MarketSwitcher } from './components/MarketSwitcher';
import { Header } from './components/Header';
import { ToastStack } from './components/ToastStack';
import { SmartMoneyTradesSidebar } from './components/SmartMoneyTradesSidebar';
import { UpgradeModal } from './components/membership/UpgradeModal';
import { BuyPointsModal } from './components/points/BuyPointsModal';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { RecommendPage } from './pages/RecommendPage';
import { FavoritesPage } from './pages/FavoritesPage';
import Positions from './pages/Positions';
import MyCopyTrading from './pages/MyCopyTrading';
import WalletsPage from './pages/WalletsPage';
import { TraderDetail } from './pages/TraderDetail';
import { InviteManagePage } from './pages/InviteManagePage';
import { useI18n } from './i18n/useI18n';

function App() {
  const {
    isAuthenticated,
    userId,
    logout,
    isTainted,
    showInviteCodeModal,
    setShowInviteCodeModal,
    membership,
    membershipLoading,
    refreshMembership,
    points,
    pointsLoading,
    refreshPoints,
  } = useAuth();
  const { toasts, pushToast } = useToast();
  const { t } = useI18n();

  // 聪明钱实时交易 WebSocket
  const { activities: smartMoneyActivities, isConnected: smartMoneyConnected } = useTraderActivityWebSocket();

  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    searchLoading,
    showSearchDropdown,
    setShowSearchDropdown,
    searchContainerRef
  } = useSearch();

  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // 默认进入牛人榜（未开通/未登录会展示锁定页引导升级/登录）
  const [activeSection, setActiveSection] = useState('leaderboard');
  const [dockOpen, setDockOpen] = useState(false);
  const [preferenceOpen, setPreferenceOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [aiInsightOpen, setAiInsightOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [totalUsdcBalance, setTotalUsdcBalance] = useState(0);
  const [isWalletBalanceLoading, setIsWalletBalanceLoading] = useState(true);
  const hasFetchedWalletOnceRef = useRef(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipTierOverride, setMembershipTierOverride] = useState(null);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [emailBindOpen, setEmailBindOpen] = useState(false);

  // 推荐页市场数据管理
  const marketDataHook = useMarketData(activeSection, pushToast);

  // 收藏功能管理
  const favoritesHook = useFavorites(isAuthenticated, userId);

  // 路由模式判断
  const traderDetailMode = location.pathname.startsWith('/traders/');
  const traderAddress = params.address;

  useEffect(() => {
    if (!isAuthenticated || membershipLoading) return;
    const email = membership?.profile?.email;
    setEmailBindOpen(!email);
  }, [isAuthenticated, membership, membershipLoading]);

  // 从 hooks 解构需要的数据和方法
  const {
    activeMarket,
    activeMarketIndex,
    marketData,
    hasNext,
    cycleMarket,
    setActiveMarketIndex,
    setMarketData,
    setPage,
    setHasNext,
    marketDataRef,
    pageRef,
    hasNextRef
  } = marketDataHook;

  const {
    favoriteSlugs,
    favoriteItems,
    setFavoriteItems,
    setFavoriteSlugs
  } = favoritesHook;

  // Define stable data load handlers to prevent infinite loops
  const handleFavoriteDetailDataLoad = useCallback((data) => {
    if (data.marketData) setMarketData(data.marketData);
    if (data.activeMarketIndex !== undefined) setActiveMarketIndex(data.activeMarketIndex);
    if (data.page !== undefined) setPage(data.page);
    if (data.hasNext !== undefined) setHasNext(data.hasNext);
    if (data.favoriteItems) setFavoriteItems(data.favoriteItems);
    if (data.favoriteSlugs) setFavoriteSlugs(data.favoriteSlugs);
  }, [setMarketData, setActiveMarketIndex, setPage, setHasNext, setFavoriteItems, setFavoriteSlugs]);

  const handleSingleMarketDataLoad = useCallback((data) => {
    if (data.marketData) setMarketData(data.marketData);
    if (data.activeMarketIndex !== undefined) setActiveMarketIndex(data.activeMarketIndex);
    if (data.page !== undefined) setPage(data.page);
    if (data.hasNext !== undefined) setHasNext(data.hasNext);
  }, [setMarketData, setActiveMarketIndex, setPage, setHasNext]);

  // 收藏详情模式处理
  const { favoriteDetailMode } = useFavoriteDetail({
    isAuthenticated,
    userId,
    activeSection,
    onSectionChange: setActiveSection,
    onDataLoad: handleFavoriteDetailDataLoad,
    pushToast
  });

  // 单市场模式处理
  const { singleMarketMode } = useSingleMarket({
    onDataLoad: handleSingleMarketDataLoad,
    pushToast
  });

  const detailModeActive = favoriteDetailMode || singleMarketMode || traderDetailMode;

  const isFavoritesListVisible = activeSection === 'favorites' && !favoriteDetailMode;
  const showMarketStepper = !(isFavoritesListVisible || activeSection === 'positions' || activeSection === 'my-copy-trading' || activeSection === 'invite' || activeSection === 'wallets' || singleMarketMode || traderDetailMode || activeSection === 'leaderboard' || activeSection === 'smart-money-trades');
  const showLeaderboardSidebar = false;
  const mainRightPaddingClass = showLeaderboardSidebar
    ? (showMarketStepper ? 'lg:pr-[460px]' : 'lg:pr-[420px]')
    : (showMarketStepper ? 'lg:pr-[80px]' : 'lg:pr-8');

  const primaryCategory = useMemo(() => {
    if (!activeMarket) return '';
    const [primary] = activeMarket.category.split('·');
    return primary.trim();
  }, [activeMarket]);

  // 推荐页缓存 (Sidebar需要)
  const recCacheRef = useRef(null);
  const isLoadingMarketDataRef = useRef(false);

  const handleSelectSearchResult = useCallback((slug) => {
    if (!slug) return;

    if (!detailModeActive && activeSection === 'recommend' && !recCacheRef.current && marketDataRef.current.length > 0) {
      recCacheRef.current = {
        data: marketDataRef.current,
        index: activeMarketIndex,
        page: pageRef.current,
        hasNext: hasNextRef.current,
      };
    }

    setActiveSection('recommend');
    setSearchTerm('');
    setShowSearchDropdown(false);
    navigate(`/market/${slug}`);
  }, [navigate, detailModeActive, activeSection, activeMarketIndex, setSearchTerm, setShowSearchDropdown]);

  const handleSelectTrader = useCallback((address) => {
    if (!address) return;
    setSearchTerm('');
    setShowSearchDropdown(false);
    navigate(`/traders/${address}`);
  }, [navigate, setSearchTerm, setShowSearchDropdown]);

  const openMembershipModal = useCallback(
    (tierOverride) => {
      if (!isAuthenticated) {
        setLoginOpen(true);
        return;
      }
      setMembershipTierOverride(tierOverride || membership?.tier_name || 'free');
      setMembershipOpen(true);
    },
    [isAuthenticated, membership?.tier_name]
  );

  const openPointsModal = useCallback(() => {
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    setPointsOpen(true);
  }, [isAuthenticated]);

  useEffect(() => {
    const handleMembershipRequired = (event) => {
      openMembershipModal(event?.detail?.currentTier);
    };

    window.addEventListener('membership-required', handleMembershipRequired);
    return () => {
      window.removeEventListener('membership-required', handleMembershipRequired);
    };
  }, [openMembershipModal]);

  // 浮动操作按钮
  const floatingActions = useMemo(() => [
    {
      key: 'ai',
      icon: FiCpu,
      label: t('actions.aiInsights'),
      onClick: () => {
        if (!isAuthenticated || !userId) {
          setLoginOpen(true);
          return;
        }
        setAiInsightOpen(true);
      }
    },
    {
      key: 'trade',
      icon: FiTrendingUp,
      label: t('actions.quickTrade'),
      onClick: () => {
        const idOrSlug = activeMarket?.api_id || activeMarket?.id || activeMarket?.slug;
        const url = buildQuickTradeDeeplink(idOrSlug);
        if (url) {
          window.open(url, '_blank', 'noopener');
          pushToast(t('toast.quickTradeOpened'), 'action');
        } else {
          pushToast(t('toast.quickTradeInvalid'), 'info');
        }
      }
    },
    {
      key: 'favorite',
      icon: FiBookmark,
      label: favoriteSlugs.has(activeMarket?.id || '') ? t('actions.favorited') : t('actions.addFavorite'),
      onClick: async () => {
        if (!isAuthenticated || !userId) {
          setLoginOpen(true);
          return;
        }
        const slug = activeMarket?.id;
        if (!slug) return;

        const isFav = favoriteSlugs.has(slug);
        try {
          await updateFavorite(userId, slug, isFav ? 'delete' : 'add');
          if (isFav) {
            setFavoriteSlugs((prev) => {
              const next = new Set(prev);
              next.delete(slug);
              return next;
            });
            setFavoriteItems((prev) => prev.filter((it) => it.slug !== slug));
            pushToast(t('toast.favoriteRemoved'), 'info');
          } else {
            setFavoriteSlugs((prev) => new Set([...prev, slug]));
            pushToast(t('toast.favoriteAdded'), 'success');
          }
        } catch (e) {
          pushToast(t('toast.operationFailed'), 'info');
        }
      }
    },
    {
      key: 'share',
      icon: FiShare2,
      label: t('actions.shareMarket'),
      onClick: () => setShareOpen(true)
    }
  ], [isAuthenticated, userId, activeMarket, favoriteSlugs, setLoginOpen, setAiInsightOpen, setShareOpen, pushToast, updateFavorite, setFavoriteSlugs, setFavoriteItems, t]);

  useEffect(() => {
    setShowSearchDropdown(false);
  }, [location.pathname, setShowSearchDropdown]);

  // 监听登录状态变化，首次登录后弹出兴趣选择弹窗

  const fetchMainWalletBalance = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setTotalUsdcBalance(0);
      setIsWalletBalanceLoading(false);
      hasFetchedWalletOnceRef.current = false;
      return;
    }

    // Avoid spinner flicker after first fetch
    if (!hasFetchedWalletOnceRef.current) {
      setIsWalletBalanceLoading(true);
    }

    try {
      const walletRes = await getUserWallets(userId);
      if (walletRes.data && Array.isArray(walletRes.data)) {
        const mainWallet = walletRes.data.find((w) => w.is_trading_wallet);
        if (mainWallet) {
          try {
            const balanceResp = await getWalletBalance(userId, mainWallet.wallet_address);
            const balance = balanceResp?.data || {};
            const total = (balance.usdc ?? 0) + (balance.usdc_native ?? 0);
            setTotalUsdcBalance(total);
          } catch (balanceErr) {
            console.warn(`获取主钱包 ${mainWallet.wallet_address} 余额失败`, balanceErr);
            setTotalUsdcBalance(0);
          }
        } else {
          setTotalUsdcBalance(0);
        }
      }
    } catch (err) {
      console.error('获取用户钱包列表失败', err);
      setTotalUsdcBalance(0);
    } finally {
      hasFetchedWalletOnceRef.current = true;
      setIsWalletBalanceLoading(false);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    fetchMainWalletBalance();
    const intervalId = setInterval(fetchMainWalletBalance, 60_000); // Refresh every minute
    return () => clearInterval(intervalId);
  }, [fetchMainWalletBalance]);


  // 内存窗口：只保留当前 market 前后各 10 个用于回溯（暂时禁用，避免复杂性）
  // useEffect(() => {
  //   if (activeMarketIndex > 10) {
  //     const sliceStart = activeMarketIndex - 10;
  //     console.log(`[内存窗口] 索引=${activeMarketIndex} 超过10，裁剪数据，保留 [${sliceStart}, ${marketData.length})`);
  //
  //     setMarketData((prev) => {
  //       const sliced = prev.slice(sliceStart);
  //       console.log(`[内存窗口] 裁剪前=${prev.length}条，裁剪后=${sliced.length}条`);
  //       return sliced;
  //     });
  //
  //     // 使用 setTimeout 延迟更新索引，避免立即触发预取 effect 清理
  //     setTimeout(() => {
  //       console.log(`[内存窗口] 更新索引从 ${activeMarketIndex} 到 10`);
  //       setActiveMarketIndex(10);
  //     }, 0);
  //   }
  // }, [activeMarketIndex, marketData.length]);

  return (
    <div className="relative min-h-screen h-screen app-shell-bg text-text overflow-hidden">
      <div className="blur-anchor" aria-hidden="true" />

      {/* Top Bar */}
      <Header
        dockOpen={dockOpen}
        isAuthenticated={isAuthenticated}
        userId={userId}
        userMenuOpen={userMenuOpen}
        membershipTier={membership?.tier_name}
        userProfile={membership?.profile}
        points={points}
        pointsLoading={pointsLoading}
        searchTerm={searchTerm}
        searchLoading={searchLoading}
        showSearchDropdown={showSearchDropdown}
        searchResults={searchResults}
        searchContainerRef={searchContainerRef}
        totalUsdcBalance={totalUsdcBalance}
        isWalletBalanceLoading={isWalletBalanceLoading}
        onDockToggle={() => setDockOpen(!dockOpen)}
        onUserMenuToggle={() => setUserMenuOpen(!userMenuOpen)}
        onSearchChange={setSearchTerm}
        onSearchFocus={() => {
          if (searchResults.length > 0 || searchTerm.trim()) {
            setShowSearchDropdown(true);
          }
        }}
        onSearchKeyDown={(event) => {
          if (event.key === 'Escape') {
            setShowSearchDropdown(false);
            event.currentTarget.blur();
          }
        }}
        onSelectSearchResult={handleSelectSearchResult}
        onSelectTrader={handleSelectTrader}
        onLoginOpen={() => setLoginOpen(true)}
        onPreferenceOpen={() => setPreferenceOpen(true)}
        onLogout={logout}
        onMembershipOpen={() => openMembershipModal(membership?.tier_name)}
        onPointsOpen={openPointsModal}
        onEmailBindOpen={() => setEmailBindOpen(true)}
      />

      {/* Left Dock */}
      <Sidebar
        dockOpen={dockOpen}
        activeSection={activeSection}
        isAuthenticated={isAuthenticated}
        favoriteDetailMode={favoriteDetailMode}
        singleMarketMode={singleMarketMode}
        traderDetailMode={traderDetailMode}
        recCacheRef={recCacheRef}
        marketDataRef={marketDataRef}
        pageRef={pageRef}
        hasNextRef={hasNextRef}
        activeMarketIndex={activeMarketIndex}
        isLoadingMarketDataRef={isLoadingMarketDataRef}
        userId={userId}
        membershipTier={membership?.tier_name}
        onClose={() => setDockOpen(false)}
        onNavigate={navigate}
        onSectionChange={setActiveSection}
        onSetMarketData={setMarketData}
        onSetActiveMarketIndex={setActiveMarketIndex}
        onSetPage={setPage}
        onSetHasNext={setHasNext}
        onSetFavoriteItems={setFavoriteItems}
        onSetFavoriteSlugs={setFavoriteSlugs}
        onLoginOpen={() => setLoginOpen(true)}
        onMembershipRequired={() => openMembershipModal(membership?.tier_name)}
        getFavorites={getFavorites}
        pushToast={pushToast}
      />

      {/* Main Content */}
      <main
        className={`absolute inset-0 top-[92px] md:top-[108px] lg:top-[128px] overflow-y-auto px-4 md:px-6 ${dockOpen ? 'lg:pl-[264px]' : 'lg:px-8'} ${mainRightPaddingClass} pb-8 z-[2] transition-all duration-300`}
      >
        <div className="w-full max-w-full lg:max-w-[1400px] mx-auto py-2 md:py-3 lg:py-0">
          {traderDetailMode ? (
            <TraderDetail
              address={traderAddress}
              onBack={() => navigate('/')}
              pushToast={pushToast}
              isAuthenticated={isAuthenticated}
              onRequireLogin={() => setLoginOpen(true)}
              onRequireMembership={openMembershipModal}
              onBuyPoints={openPointsModal}
            />
          ) : (
            <>
              {activeSection === 'leaderboard' && (
                <LeaderboardPage
                  onViewTrader={(target) => navigate(`/traders/${target}`)}
                  pushToast={pushToast}
                  isAuthenticated={isAuthenticated}
                  onRequireLogin={() => setLoginOpen(true)}
                  onRequireMembership={openMembershipModal}
                  onRefreshMembership={refreshMembership}
                  membershipTier={membership?.tier_name}
                  membershipLoading={membershipLoading}
                />
              )}
              {activeSection === 'smart-money-trades' && (
                <section className="glass-lg w-full overflow-hidden">
                  <SmartMoneyTradesSidebar
                    activities={smartMoneyActivities}
                    isConnected={smartMoneyConnected}
                    variant="page"
                    showClose={false}
                  />
                </section>
              )}
              {isFavoritesListVisible && (
                <FavoritesPage
                  favoriteItems={favoriteItems}
                  userId={userId}
                  onNavigate={(path) => navigate(path)}
                  onRemoveFavorite={async (slug) => {
                    try {
                      await updateFavorite(userId, slug, 'delete');
                      setFavoriteSlugs((prev) => { const s = new Set(prev); s.delete(slug); return s; });
                      setFavoriteItems((prev) => prev.filter((x) => x.slug !== slug));
                    } catch {}
                  }}
                  onSectionChange={setActiveSection}
                />
              )}
              {activeSection === 'positions' && <Positions pushToast={pushToast} />}
              {activeSection === 'my-copy-trading' && (
                <MyCopyTrading onRequireMembership={openMembershipModal} />
              )}
              {activeSection === 'invite' && <InviteManagePage pushToast={pushToast} />}
              {activeSection === 'wallets' && <WalletsPage pushToast={pushToast} />}
              {(activeSection === 'recommend' || favoriteDetailMode) && (
                <div className={(activeSection === 'recommend' || favoriteDetailMode) ? '' : 'hidden'}>
                  <RecommendPage
                    marketData={marketData}
                    activeMarketIndex={activeMarketIndex}
                    primaryCategory={primaryCategory}
                    isEmpty={marketData.length === 0}
                  />
                </div>
              )}
            </>
          )}

          {/* Market Switcher - Desktop */}
          <MarketSwitcher
            showDesktop={showMarketStepper}
            showMobile={false}
            dockOpen={dockOpen}
            activeMarketIndex={activeMarketIndex}
            marketDataLength={marketData.length}
            hasNext={hasNext}
            onPrev={() => cycleMarket('prev')}
            onNext={() => cycleMarket('next')}
          />
        </div>
      </main>

      {/* Mobile Nav Switch */}
      <MarketSwitcher
        showDesktop={false}
        showMobile={!(isFavoritesListVisible || activeSection === 'positions' || activeSection === 'my-copy-trading' || activeSection === 'invite' || activeSection === 'wallets' || activeSection === 'leaderboard' || activeSection === 'smart-money-trades')}
        dockOpen={dockOpen}
        activeMarketIndex={activeMarketIndex}
        marketDataLength={marketData.length}
        hasNext={hasNext}
        onPrev={() => cycleMarket('prev')}
        onNext={() => cycleMarket('next')}
      />


      {/* Floating Action Bar */}
      <FloatingActionBar
        dockOpen={dockOpen}
        isVisible={activeSection === 'recommend' && !favoriteDetailMode && marketData.length > 0}
        floatingActions={floatingActions}
        favoriteSlugs={favoriteSlugs}
        activeMarket={activeMarket}
      />

      {/* Toasts */}
      <ToastStack toasts={toasts} />

      {/* Login Modal */}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* Invite Code Modal - 污点用户强制显示 */}
      <InviteCodeModal
        open={showInviteCodeModal && isTainted}
        onSuccess={() => {
          setShowInviteCodeModal(false);
          pushToast(t('toast.inviteVerifiedWelcome'), 'success');
        }}
      />

      {/* Email Bind Modal - 登录后未绑定邮箱强制提示 */}
      <EmailBindModal
        open={emailBindOpen}
        onClose={() => setEmailBindOpen(false)}
        pushToast={pushToast}
        onSuccess={() => refreshMembership()}
      />

      {/* AI Insight Modal */}
      <AIInsightModal
        open={aiInsightOpen}
        onClose={() => setAiInsightOpen(false)}
        marketSlug={activeMarket?.id}
        marketTitle={activeMarket?.title}
      />

      {/* Share Market Modal */}
      <ShareMarketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        market={activeMarket}
      />

      {/* Category Selection Modal */}
      <CategorySelectionModal
        open={preferenceOpen}
        onClose={() => {
          setPreferenceOpen(false);
          localStorage.setItem('preference_selected', 'true');
        }}
        onSave={(categories) => {
          localStorage.setItem('preference_selected', 'true');
          pushToast(`已保存 ${categories.length} 个兴趣类别，推荐序列已刷新`, 'success');
        }}
      />

      <UpgradeModal
        open={membershipOpen}
        onClose={() => {
          setMembershipOpen(false);
          setMembershipTierOverride(null);
        }}
        currentTier={membershipTierOverride || membership?.tier_name || 'free'}
        pushToast={pushToast}
        onRequireLogin={() => setLoginOpen(true)}
        onSuccess={() => {
          setMembershipOpen(false);
          setMembershipTierOverride(null);
          setActiveSection('leaderboard');
        }}
      />

      <BuyPointsModal
        open={pointsOpen}
        onClose={() => setPointsOpen(false)}
        pushToast={pushToast}
        onRequireLogin={() => setLoginOpen(true)}
        onSuccess={() => refreshPoints?.()}
      />
    </div>
  );
}

export default App;
